import React from 'react';
import { MessageBubble } from '../MessageBubble';
import { motion, AnimatePresence } from 'framer-motion';
import type { Message } from '../../types';
import { readArtistToken } from '../../lib/supabase';

interface ChatMessagesProps {
  messages: Message[];
  isArtistView: boolean;
  hoveredMessageId: string | null;
  onMessageHover: (id: string | null) => void;
  onDeleteMessage: (id: string) => Promise<void>;
  artistAvatar?: string;
  artistName?: string;
  profiles?: Array<{id: string; name: string; avatar_url?: string}>;
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  isArtistView,
  hoveredMessageId,
  onMessageHover,
  onDeleteMessage,
  artistAvatar,
  artistName,
  profiles = [],
}) => {
  const isOwnMessage = (isAdminMessage: boolean) => {
    return (isArtistView && !isAdminMessage) || (!isArtistView && isAdminMessage);
  };

  const getMessageSenderInfo = (message: Message) => {
    if (message.isAdmin) {
      // If we have a userId, try to find the specific admin profile
      if (message.userId) {
        const adminProfile = profiles.find(p => p.id === message.userId);

        if (adminProfile) {
          // Use avatar_url from profile, or name-based mapping, or fallback
          const adminAvatarMap: Record<string, string> = {
            'philipp lützenburger': '/avatars/philipp.png',
            'philipp': '/avatars/philipp.png',
            'lukas': '/avatars/lukas.png',
            'lukas rettenegger': '/avatars/lukas.png',
            'martijn': '/avatars/martijn.png'
          };

          const nameKey = adminProfile.name.toLowerCase().trim();
          let avatarUrl = (adminProfile as any).avatar_url || adminAvatarMap[nameKey];
          if (!avatarUrl && nameKey.includes('lukas')) {
            avatarUrl = '/avatars/lukas.png';
          }
          avatarUrl = avatarUrl || '/plane_white.png';

          return {
            name: adminProfile.name,
            avatarUrl: avatarUrl
          };
        }
      }

      // Fallback for admin messages without userId or profile not found
      return {
        name: 'Admin',
        avatarUrl: '/plane_white.png'
      };
    }
    
    // For artist messages — use display name and avatar from artist token
    // only for messages sent by this specific user. Default to artist name
    // from the artists table for older messages or messages from a different user.
    const artistToken = readArtistToken();
    const currentUserId = artistToken?.user_id;
    if (currentUserId && message.userId === currentUserId) {
      return {
        name: artistToken?.display_name || artistName || 'Artist',
        avatarUrl: artistToken?.avatar_url || undefined
      };
    }
    return {
      name: artistName || 'Artist',
      avatarUrl: artistAvatar || undefined
    };
  };

  // Sort messages by creation date (oldest first)
  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  // For artist view: check if a message can be deleted by the current artist.
  // Allowed if it's the last message (no response yet) AND either:
  //   - it's the artist's own message (userId matches), OR
  //   - it's a legacy artist message without a userId (sent before user_id tracking)
  const artistToken = readArtistToken();
  const currentArtistUserId = artistToken?.user_id;
  const isLastArtistMessage = (msgIndex: number): boolean => {
    if (!isArtistView) return false;
    const msg = sortedMessages[msgIndex];
    // Must be an artist message (not admin)
    if (msg.isAdmin) return false;
    // Must be the last message (no response from anyone after it)
    if (msgIndex !== sortedMessages.length - 1) return false;
    // Must be the artist's own message OR a legacy message without userId
    if (currentArtistUserId && msg.userId === currentArtistUserId) return true;
    if (!msg.userId) return true;
    return false;
  };

  const messageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        type: "spring",
        stiffness: 200,
        damping: 20
      }
    }),
    exit: { 
      opacity: 0, 
      x: isArtistView ? -100 : 100,
      transition: {
        duration: 0.2
      }
    }
  };

  if (messages.length === 0) {
    return (
      <motion.div 
        className="flex items-center justify-center h-32 text-gray-400"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <p>No feedback yet</p>
      </motion.div>
    );
  }

  return (
    <AnimatePresence>
      {sortedMessages.map((message, index) => {
        const senderInfo = getMessageSenderInfo(message);
        return (
          <motion.div
            key={message.id}
            custom={index}
            variants={messageVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onMouseEnter={() => onMessageHover(message.id.toString())}
            onMouseLeave={() => onMessageHover(null)}
            layout
          >
            <MessageBubble
              text={message.text}
              createdAt={message.createdAt}
              isOwn={isOwnMessage(message.isAdmin)}
              readAt={message.readAt}
              isAdmin={message.isAdmin}
              senderName={senderInfo.name}
              avatarUrl={senderInfo.avatarUrl}
              onDelete={
                hoveredMessageId === message.id.toString() && (
                  (!isArtistView && message.isAdmin) ||
                  (isArtistView && isLastArtistMessage(index))
                )
                  ? () => onDeleteMessage(message.id.toString())
                  : undefined
              }
              showDeleteButton={
                hoveredMessageId === message.id.toString() && (
                  (!isArtistView && message.isAdmin) ||
                  (isArtistView && isLastArtistMessage(index))
                )
              }
            />
          </motion.div>
        );
      })}
    </AnimatePresence>
  );
};